import {
  Evaluation,
  CriteriaGroup,
  Criterion,
  Importance,
  Participant,
  ParticipantType,
} from '@/types/evaluation.types';
import { Scorecard } from '@/types/evaluation.types';
import {
  EvaluationSummary,
  SolutionScore,
  GroupScore,
  CriterionAggregateScore,
  ParticipantScore,
  ParticipantCoverageInfo,
  AttentionItem,
  ScoringLedger,
} from './scoringSummary.interfaces';
import { mapRawToValue } from './valueScale';
import { validateGroupWeights, migrateGroupWeights, updateCriterionWeights } from './criterionWeights';

/**
 * MCDA Scoring Service
 *
 * Implements Multi-Criteria Decision Analysis scoring pipeline:
 * 1. Map raw scores to normalized values (0-1)
 * 2. Aggregate across participants per criterion/solution
 * 3. Apply two-level weighting (group × criterion)
 * 4. Calculate solution totals and rankings
 * 5. Generate attention items for quality issues
 */
export class ScoringService {
  /**
   * Calculate complete evaluation summary using MCDA methodology.
   *
   * @param evaluation The evaluation to calculate results for
   * @param scorecards All scorecards for this evaluation
   * @returns Complete evaluation summary with scores, rankings, and attention items
   * @throws Error if group weights don't sum to 1.0
   */
  static calculateEvaluationSummary(
    evaluation: Evaluation,
    scorecards: Scorecard[]
  ): EvaluationSummary {
    // Migrate group weights and calculate criterion weights
    const groups = evaluation.criteriaGroups
      .map(migrateGroupWeights)
      .map(updateCriterionWeights);

    // Validate group weights sum to 1.0
    if (!validateGroupWeights(groups)) {
      throw new Error('Group weights must sum to 1.0');
    }

    // Calculate solution scores
    const solutionScores = this.calculateSolutionScores(evaluation, groups, scorecards);

    // Identify winner(s) - handle ties
    const maxScore = Math.max(...solutionScores.map(s => s.totalScore));
    const winningSolutionIds = solutionScores
      .filter(s => s.totalScore === maxScore)
      .map(s => s.solutionId);

    solutionScores.forEach(s => {
      s.isWinner = winningSolutionIds.includes(s.solutionId);
    });

    // Calculate participant coverage
    const participantCoverage = this.calculateParticipantCoverage(evaluation, scorecards);

    // Generate attention items
    const attentionItems = this.generateAttentionItems(evaluation, solutionScores);

    // Build ledger
    const ledger = this.buildScoringLedger(solutionScores);

    return {
      evaluationId: evaluation.id,
      lastCalculated: new Date(),
      solutionScores,
      winningSolutionIds,
      participantCoverage,
      attentionItems,
      ledger,
    };
  }

  /**
   * Calculate scores for all solutions using MCDA pipeline.
   */
  private static calculateSolutionScores(
    evaluation: Evaluation,
    groups: CriteriaGroup[],
    scorecards: Scorecard[]
  ): SolutionScore[] {
    return evaluation.targetSolutions
      .map((solution, index) => {
        const groupScores = this.calculateGroupScores(
          solution.id,
          groups,
          scorecards,
          evaluation.participants
        );

        const totalScore = groupScores.reduce((sum, gs) => sum + gs.weightedScore, 0);

        return {
          solutionId: solution.id,
          solutionName: solution.solutionName,
          totalScore,
          groupScores,
          isWinner: false, // Set later
          rank: index + 1, // Set later after sorting
        };
      })
      .sort((a, b) => b.totalScore - a.totalScore)
      .map((score, index) => ({ ...score, rank: index + 1 }));
  }

  /**
   * Calculate group scores for a solution.
   */
  private static calculateGroupScores(
    solutionId: string,
    groups: CriteriaGroup[],
    scorecards: Scorecard[],
    participants: Participant[]
  ): GroupScore[] {
    return groups.map(group => {
      const criterionScores = this.calculateCriterionScores(
        solutionId,
        group,
        scorecards,
        participants
      );

      // Calculate group raw score (average of criterion values)
      // Exclude Importance.None criteria
      const validScores = criterionScores.filter(
        cs => cs.averageValue !== null && cs.importance !== Importance.None
      );

      const rawScore =
        validScores.length > 0
          ? validScores.reduce((sum, cs) => sum + cs.averageValue, 0) / validScores.length
          : 0;

      const groupWeight = group.groupWeight!; // Non-null assertion safe after migration
      const weightedScore = rawScore * groupWeight;

      return {
        groupId: group.id,
        groupName: group.name,
        groupWeight,
        rawScore,
        weightedScore,
        criterionScores,
      };
    });
  }

  /**
   * Calculate criterion aggregate scores for a solution.
   */
  private static calculateCriterionScores(
    solutionId: string,
    group: CriteriaGroup,
    scorecards: Scorecard[],
    participants: Participant[]
  ): CriterionAggregateScore[] {
    return group.criteria.map(criterion => {
      const participantScores = this.getParticipantScores(
        solutionId,
        criterion,
        group,
        scorecards,
        participants
      );

      // Calculate average normalized value (exclude null)
      const validValues = participantScores
        .map(ps => ps.normalizedValue)
        .filter(v => v !== null) as number[];

      const averageValue =
        validValues.length > 0
          ? validValues.reduce((sum, v) => sum + v, 0) / validValues.length
          : 0;

      const scoredCount = validValues.length;
      const assignedCount = participantScores.length;

      const criterionWeight = criterion.criterionWeight!; // Non-null assertion safe after migration
      const globalWeight = group.groupWeight! * criterionWeight;

      return {
        criterionId: criterion.id,
        title: criterion.title,
        importance: criterion.importance,
        criterionWeight,
        globalWeight,
        averageValue,
        participantScores,
        scoredCount,
        assignedCount,
      };
    });
  }

  /**
   * Get participant scores for a criterion.
   */
  private static getParticipantScores(
    solutionId: string,
    criterion: Criterion,
    group: CriteriaGroup,
    scorecards: Scorecard[],
    participants: Participant[]
  ): ParticipantScore[] {
    return participants
      .filter(p => this.isParticipantAssignedToGroup(p, group.id))
      .map(p => {
        const scorecard = scorecards.find(
          sc => sc.solutionId === solutionId && sc.participantId === p.id
        );

        const criteriaScore = scorecard?.scores.find(s => s.criteriaId === criterion.id);

        const rawScore = criteriaScore?.score ?? null;
        const normalizedValue = mapRawToValue(rawScore, criterion.valueScale);

        return {
          participantId: p.id,
          participantName: p.name,
          rawScore,
          normalizedValue,
        };
      });
  }

  /**
   * Check if participant is assigned to a criteria group.
   */
  private static isParticipantAssignedToGroup(
    participant: Participant,
    groupId: string
  ): boolean {
    // null = assigned to all groups (Host default)
    if (participant.criteriaGroupAssignments === null) {
      return true;
    }

    // Empty array = not assigned to any groups (Viewer)
    if (participant.criteriaGroupAssignments.length === 0) {
      return false;
    }

    // Check if group is in assignments
    return participant.criteriaGroupAssignments.includes(groupId);
  }

  /**
   * Calculate participant coverage information.
   */
  private static calculateParticipantCoverage(
    evaluation: Evaluation,
    scorecards: Scorecard[]
  ): ParticipantCoverageInfo[] {
    return evaluation.participants
      .filter(p => {
        // Include Hosts only if they have scorecards
        const hasScorecards = scorecards.some(sc => sc.participantId === p.id);
        return p.participantType !== ParticipantType.Host || hasScorecards;
      })
      .map(p => {
        const participantScorecards = scorecards.filter(sc => sc.participantId === p.id);

        const assignedGroups = evaluation.criteriaGroups.filter(g =>
          this.isParticipantAssignedToGroup(p, g.id)
        );

        // Count total criteria across assigned groups (exclude Importance.None)
        const totalCriteria = assignedGroups.reduce(
          (sum, g) =>
            sum + g.criteria.filter(c => c.importance !== Importance.None).length,
          0
        );

        // Count scored criteria across all scorecards
        const scoredCriteria = participantScorecards.reduce((sum, sc) => {
          return sum + sc.scores.filter(s => s.score !== null && s.score !== 0).length;
        }, 0);

        const completionPercentage =
          totalCriteria > 0 ? (scoredCriteria / totalCriteria) * 100 : 0;

        const isSubmitted =
          participantScorecards.length > 0 &&
          participantScorecards.every(sc => sc.isSubmitted);

        return {
          participantId: p.id,
          participantName: p.name,
          participantType: p.participantType,
          assignedGroupIds: assignedGroups.map(g => g.id),
          assignedGroupNames: assignedGroups.map(g => g.name),
          hasScorecard: participantScorecards.length > 0,
          completionPercentage,
          scoredCriteriaCount: scoredCriteria,
          totalCriteriaCount: totalCriteria,
          isSubmitted,
        };
      });
  }

  /**
   * Generate attention items for quality issues.
   */
  private static generateAttentionItems(
    evaluation: Evaluation,
    solutionScores: SolutionScore[]
  ): AttentionItem[] {
    const items: AttentionItem[] = [];

    // Detect critical criteria with low scores (< 0.75 = "Meets Expectations")
    solutionScores.forEach(solution => {
      solution.groupScores.forEach(group => {
        group.criterionScores.forEach(criterion => {
          if (
            criterion.importance === Importance.Critical &&
            criterion.averageValue < 0.75 &&
            criterion.scoredCount > 0
          ) {
            items.push({
              type: 'critical-low-score',
              severity: 'error',
              message: `Critical requirement scored below "Meets Expectations"`,
              affectedItems: [
                {
                  criterionId: criterion.criterionId,
                  criterionTitle: criterion.title,
                  solutionId: solution.solutionId,
                  solutionName: solution.solutionName,
                  averageValue: criterion.averageValue,
                },
              ],
            });
          }
        });
      });
    });

    // Detect unscored criteria (excluding Importance.None)
    const allCriteria = evaluation.criteriaGroups.flatMap(g =>
      g.criteria.filter(c => c.importance !== Importance.None)
    );

    allCriteria.forEach(criterion => {
      const criterionScores = solutionScores
        .flatMap(s => s.groupScores.flatMap(g => g.criterionScores))
        .filter(cs => cs.criterionId === criterion.id);

      const totalScored = criterionScores.reduce((sum, cs) => sum + cs.scoredCount, 0);
      const totalAssigned = criterionScores.reduce(
        (sum, cs) => sum + cs.assignedCount,
        0
      );

      if (totalScored === 0) {
        items.push({
          type: 'unscored-criterion',
          severity: totalAssigned === 0 ? 'error' : 'warning',
          message:
            totalAssigned === 0
              ? `No participants assigned to score this criterion`
              : `Criterion has not been scored by any participants yet`,
          affectedItems: [
            {
              criterionId: criterion.id,
              criterionTitle: criterion.title,
              assignedCount: totalAssigned,
              noAssignments: totalAssigned === 0,
            },
          ],
        });
      }
    });

    return items;
  }

  /**
   * Build detailed scoring ledger.
   */
  private static buildScoringLedger(solutionScores: SolutionScore[]): ScoringLedger {
    // Extract groups from first solution (all solutions have same structure)
    const groups = solutionScores[0].groupScores.map(groupScore => {
      const criteria = groupScore.criterionScores.map(criterion => {
        const solutionValues = solutionScores.map(solution => {
          const solutionGroup = solution.groupScores.find(
            g => g.groupId === groupScore.groupId
          );
          const solutionCriterion = solutionGroup?.criterionScores.find(
            c => c.criterionId === criterion.criterionId
          );

          return {
            solutionId: solution.solutionId,
            averageValue: solutionCriterion?.averageValue ?? null,
            participantCount: solutionCriterion?.scoredCount ?? 0,
            contribution: solutionCriterion
              ? solutionCriterion.globalWeight * solutionCriterion.averageValue
              : 0,
          };
        });

        return {
          criterionId: criterion.criterionId,
          title: criterion.title,
          importance: criterion.importance,
          criterionWeight: criterion.criterionWeight,
          globalWeight: criterion.globalWeight,
          solutionValues,
        };
      });

      const solutionSubtotals = solutionScores.map(solution => {
        const solutionGroup = solution.groupScores.find(
          g => g.groupId === groupScore.groupId
        );
        return {
          solutionId: solution.solutionId,
          rawSubtotal: solutionGroup?.rawScore ?? 0,
          weightedSubtotal: solutionGroup?.weightedScore ?? 0,
        };
      });

      return {
        groupId: groupScore.groupId,
        groupName: groupScore.groupName,
        groupWeight: groupScore.groupWeight,
        criteria,
        solutionSubtotals,
      };
    });

    return { groups };
  }
}
